import { useRef, useState } from 'react';
import { Loader2, MapPin, Plus, Crosshair, Trash2, ExternalLink, Camera, X } from 'lucide-react';
import {
    useGetAddressVisitsQuery,
    useCreateAddressVisitMutation,
    useDeleteAddressVisitMutation,
    useUploadInvestigationDocumentMutation,
} from '@/store/api/apiSlice';
import { resolveFileUrl } from '@/utils/fileUrl';
import { MAX_INVESTIGATION_FILE_SIZE } from '@/utils/fileValidation';
import type { AddressVisitType, InvestigationAddressVisit, InvestigationStatus } from '@/types';
import { DocumentType } from '@/types';

interface Props {
    investigationId: string;
    canEdit: boolean;
    isReportSubmitted: boolean;
    status: InvestigationStatus | string;
}

const VISIT_TYPE_OPTIONS: { value: AddressVisitType; label: string }[] = [
    { value: 'home', label: 'Home' },
    { value: 'work', label: 'Workplace' },
    { value: 'reference', label: 'Reference' },
    { value: 'guarantor_1', label: 'Guarantor 1' },
    { value: 'guarantor_2', label: 'Guarantor 2' },
    { value: 'prior_employer', label: 'Prior Employer' },
    { value: 'education', label: 'Education' },
    { value: 'other', label: 'Other' },
];

const VISIT_TYPE_LABEL: Record<string, string> = {
    // Legacy single-guarantor records still display cleanly.
    guarantor: 'Guarantor',
    ...Object.fromEntries(VISIT_TYPE_OPTIONS.map((o) => [o.value, o.label])),
};

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function readErrorMessage(err: any, fallback = 'Something went wrong'): string {
    return err?.data?.message ?? err?.message ?? fallback;
}

function buildMapsUrl(lat: number | string, lng: number | string): string {
    return `https://www.google.com/maps?q=${lat},${lng}`;
}

export default function AddressVisitsPanel({ investigationId, canEdit, isReportSubmitted, status }: Props) {
    const { data: visits = [], isLoading } = useGetAddressVisitsQuery(investigationId);
    const [showLogForm, setShowLogForm] = useState(false);

    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 text-[#FF6B35] animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h3 className="text-sm font-bold text-[#1b1b1b]">Address visits</h3>
                    <p className="text-xs text-[#4e4d4d] mt-0.5">
                        Log each in-person visit you complete during the investigation. The employer will see this list
                        on the final report.
                    </p>
                </div>
                {canEdit && !isReportSubmitted && (
                    <button
                        type="button"
                        onClick={() => setShowLogForm((v) => !v)}
                        className="bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        {showLogForm ? 'Cancel' : 'Log a visit'}
                    </button>
                )}
            </div>

            {isReportSubmitted && canEdit && (
                <div className="bg-[#FFF8F4] border border-[#FF6B35]/30 rounded-lg px-4 py-3 text-xs text-[#4e4d4d]">
                    The field report has been submitted, so visits can no longer be edited or removed (audit-trail
                    integrity). Reach out to a super-admin if a correction is needed.
                </div>
            )}

            {showLogForm && canEdit && !isReportSubmitted && (
                <LogVisitForm investigationId={investigationId} onClose={() => setShowLogForm(false)} />
            )}

            <div className="flex flex-col gap-3">
                {visits.length === 0 ? (
                    <div className="bg-[#fafafa] border border-dashed border-[#e0e0e0] rounded-xl py-10 text-center">
                        <MapPin className="w-6 h-6 text-[#4e4d4d] mx-auto mb-2" />
                        <p className="text-sm font-medium text-[#1b1b1b]">No visits logged yet</p>
                        <p className="text-xs text-[#4e4d4d] mt-1">
                            {canEdit
                                ? 'Tap "Log a visit" after each in-person check to build the timeline.'
                                : 'The agent has not logged any visits for this investigation yet.'}
                        </p>
                    </div>
                ) : (
                    visits.map((v) => (
                        <VisitCard
                            key={v.id}
                            visit={v}
                            investigationId={investigationId}
                            canDelete={canEdit && !isReportSubmitted}
                            status={status}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Visit card
// ──────────────────────────────────────────────────────────────────────────────

function VisitCard({
    visit,
    investigationId,
    canDelete,
}: {
    visit: InvestigationAddressVisit;
    investigationId: string;
    canDelete: boolean;
    status: InvestigationStatus | string;
}) {
    const [deleteVisit, { isLoading: isDeleting }] = useDeleteAddressVisitMutation();
    const [deleteError, setDeleteError] = useState('');

    const handleDelete = async () => {
        if (!window.confirm('Remove this visit from the timeline? This cannot be undone.')) return;
        setDeleteError('');
        try {
            await deleteVisit({ id: investigationId, visitId: visit.id }).unwrap();
        } catch (err: any) {
            setDeleteError(readErrorMessage(err, 'Failed to delete visit.'));
        }
    };

    const mapsUrl = buildMapsUrl(visit.latitude, visit.longitude);
    const photos = Array.isArray(visit.photos) ? visit.photos : [];

    return (
        <div className="bg-white border border-[#e0e0e0] rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="inline-block text-[11px] font-medium px-2 py-0.5 rounded-md bg-[#FF6B35]/10 text-[#FF6B35] capitalize">
                            {VISIT_TYPE_LABEL[visit.visitType] ?? visit.visitType.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[11px] text-[#717680]">
                            {formatWhen(visit.visitedAt) || formatWhen(visit.createdAt)}
                        </span>
                        {typeof visit.accuracyMeters === 'number' && (
                            <span className="text-[11px] text-[#717680]">±{visit.accuracyMeters} m</span>
                        )}
                    </div>
                    <p className="text-sm font-semibold text-[#1b1b1b] leading-snug">{visit.observedAddress}</p>
                    <p className="text-[11px] font-mono text-[#717680] mt-0.5">
                        {Number(visit.latitude).toFixed(5)}, {Number(visit.longitude).toFixed(5)}
                    </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                    <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#1b1b1b] border border-[#e0e0e0] hover:bg-gray-50 px-2.5 py-1.5 rounded-lg"
                    >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Map
                    </a>
                    {canDelete && (
                        <button
                            type="button"
                            onClick={handleDelete}
                            disabled={isDeleting}
                            className="inline-flex items-center justify-center text-red-500 hover:bg-red-50 w-8 h-8 rounded-lg disabled:opacity-50"
                            title="Remove visit"
                        >
                            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                    )}
                </div>
            </div>

            {visit.notes && (
                <p className="text-sm text-[#4e4d4d] whitespace-pre-wrap leading-relaxed mt-2">{visit.notes}</p>
            )}

            {photos.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                    {photos.map((url, i) => {
                        const resolved = resolveFileUrl(url);
                        return (
                            <a
                                key={`${url}-${i}`}
                                href={resolved}
                                target="_blank"
                                rel="noreferrer"
                                className="block w-16 h-16 rounded-md overflow-hidden border border-[#e0e0e0] bg-[#f5f5f5]"
                                title={`Photo ${i + 1}`}
                            >
                                <img src={resolved} alt="" className="w-full h-full object-cover" />
                            </a>
                        );
                    })}
                </div>
            )}

            {deleteError && <p className="text-xs text-red-600 mt-2">{deleteError}</p>}
        </div>
    );
}

// ──────────────────────────────────────────────────────────────────────────────
// Log visit form
// ──────────────────────────────────────────────────────────────────────────────

function LogVisitForm({ investigationId, onClose }: { investigationId: string; onClose: () => void }) {
    const [createVisit, { isLoading }] = useCreateAddressVisitMutation();
    const [uploadDocument] = useUploadInvestigationDocumentMutation();
    const [visitType, setVisitType] = useState<AddressVisitType>('home');
    const [observedAddress, setObservedAddress] = useState('');
    const [notes, setNotes] = useState('');
    const [latitude, setLatitude] = useState<number | null>(null);
    const [longitude, setLongitude] = useState<number | null>(null);
    const [accuracyMeters, setAccuracyMeters] = useState<number | null>(null);
    const [photos, setPhotos] = useState<string[]>([]);
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
    const [isLocating, setIsLocating] = useState(false);
    const [error, setError] = useState('');
    const photoInputRef = useRef<HTMLInputElement>(null);

    const captureLocation = () => {
        if (!('geolocation' in navigator)) {
            setError('This device does not support geolocation.');
            return;
        }
        setError('');
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLatitude(pos.coords.latitude);
                setLongitude(pos.coords.longitude);
                setAccuracyMeters(Math.round(pos.coords.accuracy));
                setIsLocating(false);
            },
            (err) => {
                setIsLocating(false);
                setError(
                    err.code === err.PERMISSION_DENIED
                        ? 'Location permission denied. Enable it in your browser settings and try again.'
                        : 'Could not capture your location. Try again or move outdoors.',
                );
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
        );
    };

    // Upload each selected image through the investigation document flow and
    // attach the resulting file URL to the visit. The URL is what the backend
    // stores on the visit and what the employer report renders.
    const handlePhotosSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (files.length === 0) return;
        setError('');

        const valid: File[] = [];
        for (const file of files) {
            if (!file.type.startsWith('image/')) {
                setError(`"${file.name}" is not an image. Only image files can be attached to a visit.`);
                continue;
            }
            if (file.size > MAX_INVESTIGATION_FILE_SIZE) {
                setError(`"${file.name}" is larger than 10 MB. Please choose a smaller photo.`);
                continue;
            }
            valid.push(file);
        }
        if (valid.length === 0) return;

        setIsUploadingPhoto(true);
        try {
            for (const file of valid) {
                const doc = await uploadDocument({
                    id: investigationId,
                    file,
                    documentType: DocumentType.OTHER,
                }).unwrap();
                const url = doc?.fileUrl;
                if (url) {
                    setPhotos((prev) => [...prev, url]);
                } else {
                    setError('The photo uploaded but no file URL came back. Please try again.');
                }
            }
        } catch (err: any) {
            setError(readErrorMessage(err, 'Failed to upload photo. Please try again.'));
        } finally {
            setIsUploadingPhoto(false);
        }
    };

    const removePhoto = (i: number) => {
        setPhotos((prev) => prev.filter((_, idx) => idx !== i));
    };

    const submit = async () => {
        if (!observedAddress.trim()) {
            setError('Observed address is required.');
            return;
        }
        if (latitude === null || longitude === null) {
            setError('Capture your current location before saving.');
            return;
        }
        setError('');
        try {
            await createVisit({
                id: investigationId,
                data: {
                    visitType,
                    observedAddress: observedAddress.trim(),
                    latitude,
                    longitude,
                    ...(accuracyMeters !== null ? { accuracyMeters } : {}),
                    ...(notes.trim() ? { notes: notes.trim() } : {}),
                    ...(photos.length > 0 ? { photos } : {}),
                },
            }).unwrap();
            onClose();
        } catch (err: any) {
            setError(readErrorMessage(err, 'Failed to log visit.'));
        }
    };

    return (
        <div className="bg-white border border-[#e0e0e0] rounded-xl p-5">
            <h4 className="text-sm font-bold text-[#1b1b1b] mb-3">Log a visit</h4>

            <div className="flex flex-col gap-3">
                <div className="flex flex-col sm:flex-row gap-3">
                    <label className="flex-1 min-w-0">
                        <span className="block text-xs font-medium text-[#1b1b1b] mb-1">Visit type</span>
                        <select
                            value={visitType}
                            onChange={(e) => setVisitType(e.target.value as AddressVisitType)}
                            className="w-full bg-white border border-[#e0e0e0] rounded-lg h-10 px-3 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15"
                        >
                            {VISIT_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>

                <label>
                    <span className="block text-xs font-medium text-[#1b1b1b] mb-1">Observed address</span>
                    <textarea
                        value={observedAddress}
                        onChange={(e) => {
                            setObservedAddress(e.target.value);
                            setError('');
                        }}
                        rows={2}
                        placeholder="12 Adeola Odeku St, Victoria Island, Lagos"
                        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y"
                    />
                </label>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                        type="button"
                        onClick={captureLocation}
                        disabled={isLocating}
                        className="bg-[#151515] hover:bg-[#2a2a2a] disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 shrink-0"
                    >
                        {isLocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                        {latitude !== null ? 'Re-capture location' : 'Capture location'}
                    </button>
                    {latitude !== null && longitude !== null && (
                        <div className="text-xs text-[#4e4d4d]">
                            <span className="font-mono">
                                {latitude.toFixed(5)}, {longitude.toFixed(5)}
                            </span>
                            {accuracyMeters !== null && <> &middot; ±{accuracyMeters} m</>}{' '}
                            <a
                                href={buildMapsUrl(latitude, longitude)}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-[#FF6B35] hover:underline"
                            >
                                view
                            </a>
                        </div>
                    )}
                </div>

                <label>
                    <span className="block text-xs font-medium text-[#1b1b1b] mb-1">Notes</span>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        placeholder="What did you observe at the address? Who was present?"
                        className="w-full bg-white border border-[#e0e0e0] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/15 resize-y"
                    />
                </label>

                <div>
                    <span className="block text-xs font-medium text-[#1b1b1b] mb-1">Photos (optional)</span>
                    <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handlePhotosSelected}
                        className="hidden"
                    />
                    <button
                        type="button"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={isUploadingPhoto}
                        className="bg-white border border-[#e0e0e0] hover:bg-gray-50 disabled:opacity-60 text-[#1b1b1b] px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                        {isUploadingPhoto ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Camera className="w-4 h-4" />
                        )}
                        {isUploadingPhoto ? 'Uploading photo' : 'Add photo'}
                    </button>
                    {photos.length > 0 && (
                        <div className="mt-2 flex gap-2 flex-wrap">
                            {photos.map((url, i) => (
                                <div
                                    key={`${url}-${i}`}
                                    className="relative w-16 h-16 rounded-md overflow-hidden border border-[#e0e0e0] bg-[#f5f5f5]"
                                >
                                    <img src={resolveFileUrl(url)} alt="" className="w-full h-full object-cover" />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(i)}
                                        className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 text-white w-5 h-5 rounded-full flex items-center justify-center"
                                        title="Remove photo"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-[#717680] mt-1">
                        Photos taken at the address are attached to this visit and appear on the final report.
                    </p>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                        {error}
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        className="px-4 py-2 border border-[#e0e0e0] rounded-lg text-sm font-medium text-[#1b1b1b] hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={isLoading || isUploadingPhoto}
                        className="bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                        {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                        Save visit
                    </button>
                </div>
            </div>
        </div>
    );
}
